// src/pages/ContractInsightStatus.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  pollReport,
  getContractInsightStatus,
  getLiveView,
  getSourceFile,
  getReport,
  createContractInsightBrowserTicket,
} from "../utils/contractApi";
import ContractFileViewer from "../components/ContractFileViewer";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const STATUS_UI = {
  queued: {
    key: "queued",
    label: "Queued",
    shortLabel: "Queued",
    text: "Your contract is waiting to start.",
    color: "#64748B",
    bg: "#F8FAFC",
    border: "#CBD5E1",
    percent: 6,
  },
  dispatch_reserved: {
    key: "dispatch_reserved",
    label: "Dispatch reserved",
    shortLabel: "Reserved",
    text: "Your contract analysis is getting ready to start.",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    percent: 10,
  },
  worker_received: {
    key: "worker_received",
    label: "Received",
    shortLabel: "Received",
    text: "Your contract analysis has started.",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    percent: 12,
  },
  ingesting: {
    key: "ingesting",
    label: "Reading contract",
    shortLabel: "Reading",
    text: "Your uploaded contract is being opened, normalized, and page-counted.",
    color: "#0EA5E9",
    bg: "#ECFEFF",
    border: "#A5F3FC",
    percent: 25,
  },
  analyzing: {
    key: "analyzing",
    label: "Analyzing clauses",
    shortLabel: "Analyzing",
    text: "Your contract is being reviewed for important clauses, risk criteria, and obligations.",
    color: "#8B5CF6",
    bg: "#F5F3FF",
    border: "#DDD6FE",
    percent: 55,
  },
  building_report: {
    key: "building_report",
    label: "Building report",
    shortLabel: "Building",
    text: "The final Contract Insight report is being assembled.",
    color: "#F59E0B",
    bg: "#FFFBEB",
    border: "#FDE68A",
    percent: 75,
  },
  rendering_pdf: {
    key: "rendering_pdf",
    label: "Rendering PDF",
    shortLabel: "PDF",
    text: "Your styled PDF report is being rendered.",
    color: "#D97706",
    bg: "#FFF7ED",
    border: "#FED7AA",
    percent: 88,
  },
  saving_report: {
    key: "saving_report",
    label: "Saving report",
    shortLabel: "Saving",
    text: "Your report files are being saved.",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#BBF7D0",
    percent: 93,
  },
  finalizing_billing: {
    key: "finalizing_billing",
    label: "Finalizing billing",
    shortLabel: "Billing",
    text: "Final checks are being completed after the report is saved.",
    color: "#0F766E",
    bg: "#F0FDFA",
    border: "#99F6E4",
    percent: 96,
  },
  saving_live_view: {
    key: "saving_live_view",
    label: "Saving Live View",
    shortLabel: "Live View",
    text: "Dashboard highlights are being saved.",
    color: "#0891B2",
    bg: "#ECFEFF",
    border: "#A5F3FC",
    percent: 97,
  },
  complete: {
    key: "complete",
    label: "Ready",
    shortLabel: "Ready",
    text: "Your Contract Insight report is ready.",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#BBF7D0",
    percent: 100,
  },
  error: {
    key: "error",
    label: "Needs attention",
    shortLabel: "Error",
    text: "The analysis could not be completed.",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    percent: 100,
  },
  billing_error: {
    key: "billing_error",
    label: "Needs attention",
    shortLabel: "Billing",
    text: "The report needs one more check before it is ready.",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    percent: 100,
  },
};

const STATUS_STEPS = [
  STATUS_UI.worker_received,
  STATUS_UI.ingesting,
  STATUS_UI.analyzing,
  STATUS_UI.building_report,
  STATUS_UI.rendering_pdf,
  STATUS_UI.saving_report,
  STATUS_UI.finalizing_billing,
  STATUS_UI.saving_live_view,
  STATUS_UI.complete,
];

const STATUS_INDEX = STATUS_STEPS.reduce((acc, item, idx) => {
  acc[item.key] = idx;
  return acc;
}, {});

const STATUS_DISPLAY_STEPS = [
  {
    key: "received",
    label: "Received",
    shortLabel: "Received",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    statuses: ["queued", "dispatch_reserved", "worker_received"],
  },
  {
    key: "reading",
    label: "Reading",
    shortLabel: "Reading",
    color: "#0EA5E9",
    bg: "#ECFEFF",
    border: "#A5F3FC",
    statuses: ["ingesting"],
  },
  {
    key: "analyzing",
    label: "Analyzing",
    shortLabel: "Analyzing",
    color: "#8B5CF6",
    bg: "#F5F3FF",
    border: "#DDD6FE",
    statuses: ["analyzing"],
  },
  {
    key: "preparing_report",
    label: "Preparing report",
    shortLabel: "Report",
    color: "#F59E0B",
    bg: "#FFFBEB",
    border: "#FDE68A",
    statuses: ["building_report", "rendering_pdf"],
  },
  {
    key: "finalizing",
    label: "Finalizing",
    shortLabel: "Ready",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#BBF7D0",
    statuses: ["saving_report", "finalizing_billing", "saving_live_view", "complete"],
  },
];

const STATUS_ALIASES = {
  awaiting_upload: "queued",
  upload_url_issued: "queued",
  received: "worker_received",
  uploaded: "queued",
  dispatched: "dispatch_reserved",
  processing: "analyzing",
  extracting: "ingesting",
  assembling: "building_report",
  finalizing: "saving_live_view",
  completed: "complete",
  report_ready: "complete",
  failed: "error",
  failure: "error",
};

const FRIENDLY_STATUS_ERROR =
  "Something went wrong while preparing your report. Please try again later or return from Analysis History.";

const FRIENDLY_PREVIEW_ERROR =
  "This preview is not available right now. Please try again later or open the dashboard from Analysis History.";

const FRIENDLY_SUMMARY_ERROR =
  "The dashboard summary is not available right now. Please open the dashboard or try again later.";

function normalizeStatus(rawStatus, rawProgress) {
  const raw = String(rawStatus || rawProgress?.stage || "").trim().toLowerCase();
  if (!raw) return "queued";
  return STATUS_ALIASES[raw] || raw;
}

function getStageMeta(status, progress) {
  const normalized = normalizeStatus(status, progress);
  return STATUS_UI[normalized] || STATUS_UI.queued;
}

function statusIndex(status, progress) {
  const normalized = normalizeStatus(status, progress);
  if (normalized === "error" || normalized === "billing_error") return -1;
  if (Object.prototype.hasOwnProperty.call(STATUS_INDEX, normalized)) {
    return STATUS_INDEX[normalized];
  }
  if (normalized === "dispatch_reserved" || normalized === "queued") return -1;
  return -1;
}

function displayStageIndex(status, progress) {
  const normalized = normalizeStatus(status, progress);
  if (normalized === "error" || normalized === "billing_error") return -1;
  if (normalized === "complete") return STATUS_DISPLAY_STEPS.length - 1;

  const idx = STATUS_DISPLAY_STEPS.findIndex((step) =>
    step.statuses.includes(normalized)
  );
  return idx >= 0 ? idx : 0;
}

function friendly(status, progress) {
  return getStageMeta(status, progress).label;
}

function cleanDisplayText(value, fallback = "—") {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const joined = value.map((item) => cleanDisplayText(item, "")).filter(Boolean).join(", ");
    return joined || fallback;
  }
  if (typeof value === "object") {
    const preferred = value.message || value.error || value.title || value.label || value.name || value.summary || value.text;
    return preferred ? cleanDisplayText(preferred, fallback) : fallback;
  }
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  if (/^\{[\s\S]*\}$/.test(text) || /^\[[\s\S]*\]$/.test(text)) return fallback;
  return text;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
}

function pickFirstNumeric(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
  }
  return "";
}

function getByPath(obj, path, fallback = "") {
  const parts = String(path || "").split(".").filter(Boolean);
  let cur = obj;
  for (const part of parts) {
    if (cur === undefined || cur === null) return fallback;
    cur = cur?.[part];
  }
  return cur === undefined || cur === null || cur === "" ? fallback : cur;
}

function asPages(liveView) {
  if (Array.isArray(liveView?.pages)) return liveView.pages;
  if (Array.isArray(liveView?.liveView?.pages)) return liveView.liveView.pages;
  if (Array.isArray(liveView?.live_view?.pages)) return liveView.live_view.pages;
  return [];
}

function countParagraphsFromPages(pages = []) {
  if (!Array.isArray(pages)) return 0;
  return pages.reduce((total, page) => {
    const paragraphs = Array.isArray(page?.paragraphs) ? page.paragraphs : [];
    return total + paragraphs.length;
  }, 0);
}


function normalizeCoverageCount(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }
  if (typeof value === "string" && value.trim() !== "") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return Math.max(0, Math.trunc(numeric));
  }
  return null;
}

function getCoverageSnapshotFromPayload(...sources) {
  const candidates = [];

  for (const source of sources) {
    if (!isPlainObject(source)) continue;

    candidates.push(
      source?.coverageSummary,
      source?.coverage_summary,
      getByPath(source, "report.coverageSummary", null),
      getByPath(source, "report.coverage_summary", null),
      getByPath(source, "report.summaryReport.executiveSnapshot.coverage", null),
      getByPath(source, "report.summary_report.executive_snapshot.coverage", null),
      getByPath(source, "summaryReport.executiveSnapshot.coverage", null),
      getByPath(source, "summary_report.executive_snapshot.coverage", null)
    );
  }

  for (const candidate of candidates) {
    if (!isPlainObject(candidate)) continue;

    const found = normalizeCoverageCount(
      pickFirst(candidate?.found, candidate?.present)
    );
    const unclear = normalizeCoverageCount(
      pickFirst(candidate?.unclear, candidate?.partial)
    );
    const notConfirmed = normalizeCoverageCount(
      pickFirst(candidate?.notConfirmed, candidate?.not_confirmed, candidate?.unknown)
    );
    const notFound = normalizeCoverageCount(
      pickFirst(candidate?.notFound, candidate?.not_found, candidate?.absent)
    );

    if ([found, unclear, notConfirmed, notFound].some((value) => value !== null)) {
      return { found, unclear, notConfirmed, notFound };
    }
  }

  return {
    found: null,
    unclear: null,
    notConfirmed: null,
    notFound: null,
  };
}

function normalizeSummaryCandidate(value) {
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "string") return cleanDisplayText(value, "");
  if (typeof value === "number" || typeof value === "boolean") return cleanDisplayText(value, "");

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => {
        if (typeof item === "string") return cleanDisplayText(item, "");
        if (isPlainObject(item)) {
          return normalizeSummaryCandidate(
            pickFirst(
              item?.dashboardSummary,
              item?.dashboard_summary,
              item?.executiveSummary,
              item?.executive_summary,
              item?.overallSummary,
              item?.overall_summary,
              item?.overallFinding,
              item?.overall_finding,
              item?.summary,
              item?.text,
              item?.body,
              item?.content,
              item?.finding,
              item?.description
            )
          );
        }
        return "";
      })
      .filter(Boolean);

    return cleanDisplayText(parts.join(" "), "");
  }

  if (isPlainObject(value)) {
    const preferred = pickFirst(
      value?.dashboardSummary,
      value?.dashboard_summary,
      value?.executiveSummary,
      value?.executive_summary,
      value?.overallSummary,
      value?.overall_summary,
      value?.overallFinding,
      value?.overall_finding,
      value?.summaryText,
      value?.summary_text,
      value?.text,
      value?.body,
      value?.content,
      value?.overview,
      value?.conclusion,
      value?.finding,
      value?.description,
      value?.summary
    );

    if (preferred && preferred !== value) return normalizeSummaryCandidate(preferred);
  }

  return "";
}

function extractSummaryFromSections(sections) {
  if (!sections) return "";

  const sectionMatches = (section) => {
    const label = cleanDisplayText(
      pickFirst(section?.title, section?.heading, section?.label, section?.name, section?.sectionTitle),
      ""
    ).toLowerCase();

    return (
      label.includes("dashboard summary") ||
      label.includes("executive summary") ||
      label === "summary" ||
      label.includes("overall finding") ||
      label.includes("overall summary")
    );
  };

  if (Array.isArray(sections)) {
    for (const section of sections) {
      if (!isPlainObject(section) || !sectionMatches(section)) continue;
      const text = normalizeSummaryCandidate(
        pickFirst(
          section?.dashboardSummary,
          section?.dashboard_summary,
          section?.executiveSummary,
          section?.executive_summary,
          section?.overallFinding,
          section?.overall_finding,
          section?.summary,
          section?.text,
          section?.body,
          section?.content,
          section?.paragraphs,
          section?.items
        )
      );
      if (text) return text;
    }
  }

  if (isPlainObject(sections)) {
    for (const [key, section] of Object.entries(sections)) {
      const keyText = String(key || "").replace(/[_-]+/g, " ").toLowerCase();
      const keyLooksRight =
        keyText.includes("dashboard summary") ||
        keyText.includes("executive summary") ||
        keyText.includes("overall finding") ||
        keyText.includes("overall summary") ||
        keyText === "summary";

      if (!keyLooksRight && !(isPlainObject(section) && sectionMatches(section))) continue;

      const text = normalizeSummaryCandidate(section);
      if (text) return text;
    }
  }

  return "";
}

function extractSummaryTextFromObject(obj) {
  if (!isPlainObject(obj)) return "";

  const directCandidates = [
    obj?.dashboardSummary,
    obj?.dashboard_summary,
    obj?.executiveSummary,
    obj?.executive_summary,
    obj?.overallSummary,
    obj?.overall_summary,
    obj?.overallFinding,
    obj?.overall_finding,
    obj?.summaryText,
    obj?.summary_text,
    obj?.summary,
    obj?.overview,
    obj?.conclusion,
  ];

  for (const candidate of directCandidates) {
    const text = normalizeSummaryCandidate(candidate);
    if (text) return text;
  }

  const pathCandidates = [
    "meta.dashboardSummary",
    "meta.dashboard_summary",
    "meta.executiveSummary",
    "meta.executive_summary",
    "metadata.dashboardSummary",
    "metadata.dashboard_summary",
    "metadata.executiveSummary",
    "metadata.executive_summary",
    "metadata.summary",
    "dashboard.summary",
    "dashboard.dashboardSummary",
    "dashboard.dashboard_summary",
    "dashboard.executiveSummary",
    "dashboard.executive_summary",
    "summary.overallFinding",
    "summary.overall_finding",
    "summary.executiveSummary",
    "summary.executive_summary",
    "summary.dashboardSummary",
    "summary.dashboard_summary",
    "summary.text",
    "summary.summary",
    "executiveSummary.text",
    "executive_summary.text",
    "report.summary.overallFinding",
    "report.summary.overall_finding",
    "report.summary.executiveSummary",
    "report.summary.executive_summary",
    "report.summary.dashboardSummary",
    "report.summary.dashboard_summary",
    "report.executiveSummary",
    "report.executive_summary",
    "report.dashboardSummary",
    "report.dashboard_summary",
    "finalReport.summary",
    "final_report.summary",
    "finalReport.executiveSummary",
    "final_report.executive_summary",
    "finalReport.dashboardSummary",
    "final_report.dashboard_summary",
    "liveView.dashboardSummary",
    "liveView.dashboard_summary",
    "liveView.executiveSummary",
    "liveView.executive_summary",
    "live_view.dashboardSummary",
    "live_view.dashboard_summary",
    "live_view.executiveSummary",
    "live_view.executive_summary",
  ];

  for (const path of pathCandidates) {
    const text = normalizeSummaryCandidate(getByPath(obj, path));
    if (text) return text;
  }

  return (
    extractSummaryFromSections(obj?.sections) ||
    extractSummaryFromSections(obj?.reportSections) ||
    extractSummaryFromSections(obj?.report_sections) ||
    extractSummaryFromSections(obj?.finalReport?.sections) ||
    extractSummaryFromSections(obj?.final_report?.sections) ||
    ""
  );
}

function getEmbeddedReportBody(data) {
  if (!isPlainObject(data)) return null;

  const candidates = [
    data?.reportBody,
    data?.report_body,
    data?.reportJson,
    data?.report_json,
    data?.reportData,
    data?.report_data,
    data?.report,
    data,
  ];

  for (const candidate of candidates) {
    if (!isPlainObject(candidate)) continue;

    const hasBodyLikeContent = Boolean(
      extractSummaryTextFromObject(candidate) ||
        candidate?.sections ||
        candidate?.analysis ||
        candidate?.risk ||
        candidate?.riskScore ||
        candidate?.risk_score
    );

    const onlyLooksLikePathWrapper =
      Object.keys(candidate).length <= 3 &&
      (candidate?.reportJsonPath ||
        candidate?.report_json_path ||
        candidate?.reportPdfPath ||
        candidate?.report_pdf_path);

    if (hasBodyLikeContent && !onlyLooksLikePathWrapper) return candidate;
  }

  return null;
}

function getReportJsonUrlFromPayload(data) {
  return pickFirst(
    data?.reportJsonUrl,
    data?.report_json_url,
    getByPath(data, "artifacts.reportJsonUrl"),
    getByPath(data, "artifacts.report_json_url"),
    getByPath(data, "report.reportJsonUrl"),
    getByPath(data, "report.report_json_url"),
    getByPath(data, "job.reportJsonUrl"),
    getByPath(data, "job.report_json_url"),
    getByPath(data, "job.artifacts.reportJsonUrl"),
    getByPath(data, "job.artifacts.report_json_url")
  );
}

function getAnalyzedPdfUrlFromPayload(data) {
  return pickFirst(
    data?.analyzedPdfUrl,
    data?.analyzed_pdf_url,
    data?.ingestPdfUrl,
    data?.ingest_pdf_url,
    data?.normalizedPdfUrl,
    data?.normalized_pdf_url,
    data?.canonicalPdfUrl,
    data?.canonical_pdf_url,
    getByPath(data, "ingest.analyzedPdfUrl"),
    getByPath(data, "ingest.analyzed_pdf_url"),
    getByPath(data, "ingest.ingestPdfUrl"),
    getByPath(data, "ingest.ingest_pdf_url"),
    getByPath(data, "ingest.normalizedPdfUrl"),
    getByPath(data, "ingest.normalized_pdf_url"),
    getByPath(data, "ingest.canonicalPdfUrl"),
    getByPath(data, "ingest.canonical_pdf_url"),
    getByPath(data, "artifacts.analyzedPdfUrl"),
    getByPath(data, "artifacts.analyzed_pdf_url"),
    getByPath(data, "artifacts.ingestPdfUrl"),
    getByPath(data, "artifacts.ingest_pdf_url"),
    getByPath(data, "artifacts.normalizedPdfUrl"),
    getByPath(data, "artifacts.normalized_pdf_url"),
    getByPath(data, "job.analyzedPdfUrl"),
    getByPath(data, "job.analyzed_pdf_url"),
    getByPath(data, "job.ingestPdfUrl"),
    getByPath(data, "job.ingest_pdf_url"),
    getByPath(data, "job.normalizedPdfUrl"),
    getByPath(data, "job.normalized_pdf_url"),
    getByPath(data, "job.ingest.analyzedPdfUrl"),
    getByPath(data, "job.ingest.analyzed_pdf_url"),
    getByPath(data, "job.ingest.ingestPdfUrl"),
    getByPath(data, "job.ingest.ingest_pdf_url"),
    getByPath(data, "job.ingest.normalizedPdfUrl"),
    getByPath(data, "job.ingest.normalized_pdf_url"),
    getByPath(data, "job.artifacts.analyzedPdfUrl"),
    getByPath(data, "job.artifacts.analyzed_pdf_url"),
    getByPath(data, "job.artifacts.ingestPdfUrl"),
    getByPath(data, "job.artifacts.ingest_pdf_url"),
    getByPath(data, "job.artifacts.normalizedPdfUrl"),
    getByPath(data, "job.artifacts.normalized_pdf_url")
  );
}

function hideLiveViewParagraphReference(value, paraId = "") {
  const text = cleanDisplayText(value, "");
  if (!text) return "";

  const id = String(paraId || "").trim();
  if (!id) return text;

  const escapedId = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text
    .replace(new RegExp(`^\\s*(?:#|§)?\\s*${escapedId}\\s*(?:[-–—:|]\\s*)?`, "i"), "")
    .trim();
}

async function fetchReportJsonBodyFromSignedUrl(url) {
  if (!url) return null;

  const response = await fetch(url, {
    method: "GET",
    credentials: "omit",
  });

  if (!response.ok) throw new Error(`Report JSON URL returned ${response.status}.`);
  return response.json();
}

function buildLiveViewSummary({ pageCount, paragraphCount, ready }) {
  if (!ready) {
    return "This Contract Insight report is still processing. You may safely leave this page and return from Analysis History.";
  }

  const pagesText = pageCount ? `${pageCount} page${Number(pageCount) === 1 ? "" : "s"}` : "the analyzed pages";
  const highlightsText = paragraphCount
    ? `${paragraphCount} Live View highlight${Number(paragraphCount) === 1 ? "" : "s"}`
    : "Live View highlights";

  return `This contract includes ${highlightsText} across ${pagesText}. Review the original contract beside the Live View to understand key clause highlights and explanations.`;
}


function formatJobShort(jobId) {
  const text = String(jobId || "").trim();
  if (!text) return "JOB";
  if (text.length <= 24) return text;
  return `${text.slice(0, 16)}…${text.slice(-6)}`;
}

function Spinner({ size = 22, color = "#2563EB" }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        border: `2px solid ${color}22`,
        borderTopColor: color,
        borderRadius: "999px",
        display: "inline-block",
        animation: "contractStatusSpin .8s linear infinite",
      }}
    />
  );
}

export default function ContractInsightStatus() {
  const q = useQuery();
  const location = useLocation();
  const jobId = q.get("jobId") || "";
  const navigate = useNavigate();

  const routeStateToken =
    typeof location.state?.idToken === "string" ? location.state.idToken : "";
  const idTokenRef = useRef(routeStateToken || "");

  const [idToken, setIdToken] = useState(routeStateToken || "");
  const [authReady, setAuthReady] = useState(Boolean(routeStateToken));
  const [authError, setAuthError] = useState("");
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [liveView, setLiveView] = useState(null);
  const [liveViewPayload, setLiveViewPayload] = useState(null);
  const [liveViewError, setLiveViewError] = useState("");
  const [liveViewLoading, setLiveViewLoading] = useState(false);

  const [reportRoutePayload, setReportRoutePayload] = useState(null);
  const [reportJsonBody, setReportJsonBody] = useState(null);
  const [reportPayloadLoading, setReportPayloadLoading] = useState(false);
  const [reportPayloadError, setReportPayloadError] = useState("");
  const [artifactLoading, setArtifactLoading] = useState("");
  const [artifactError, setArtifactError] = useState("");

  const [source, setSource] = useState(null);
  const [sourceError, setSourceError] = useState("");
  const [sourceLoading, setSourceLoading] = useState(false);

  const [expandedTileKey, setExpandedTileKey] = useState(null);
  const [expandedPageKey, setExpandedPageKey] = useState(null);

  const [isDesktopLike, setIsDesktopLike] = useState(true);

  const [desktopViewMode, setDesktopViewMode] = useState("both");
  const [activeMobileTab, setActiveMobileTab] = useState("contract");
  const [showProcessingDetails, setShowProcessingDetails] = useState(false);
  const [documentView, setDocumentView] = useState("analyzed");

  const selectDocumentView = (nextView) => {
    setDocumentView(nextView === "original" ? "original" : "analyzed");
  };

  useEffect(() => {
    setDocumentView("analyzed");
  }, [jobId]);

  useEffect(() => {
    if (!jobId) {
      navigate("/contract-insight", { replace: true });
    }
  }, [jobId, navigate]);

  const getFreshAuthToken = async ({ forceRefresh = false } = {}) => {
    const user = getAuth()?.currentUser;
    if (!user) {
      throw new Error("Please log in again to view this Contract Insight job.");
    }
    const token = await user.getIdToken(forceRefresh);
    idTokenRef.current = token;
    setIdToken(token);
    setAuthReady(true);
    setAuthError("");
    return token;
  };

  const openSummaryReport = async () => {
    if (!jobId || artifactLoading) return;

    setArtifactLoading("summary-pdf");
    setArtifactError("");

    // Open synchronously from the user's click so the browser does not
    // block the tab while we fetch a fresh opaque AskLawly browser ticket.
    const browserTab = window.open("about:blank", "_blank");
    if (browserTab) {
      try {
        browserTab.opener = null;
        browserTab.document.title = "Opening Summary Report…";
      } catch (_) {
        // Navigation still works even if the temporary blank tab is restricted.
      }
    }

    try {
      const token = idTokenRef.current || idToken || (await getFreshAuthToken());

      const ticket = await createContractInsightBrowserTicket(
        jobId,
        "summary",
        { idToken: token }
      );

      const browserUrl = String(ticket?.browserUrl || "").trim();
      if (!browserUrl) {
        throw new Error(
          "Summary Report browser access is not available for this analysis yet."
        );
      }

      if (browserTab && !browserTab.closed) {
        browserTab.location.replace(browserUrl);
      } else {
        window.open(browserUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (browserTab && !browserTab.closed) {
        browserTab.close();
      }
      setArtifactError(err?.message || "Unable to open Summary Report.");
    } finally {
      setArtifactLoading("");
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        idTokenRef.current = "";
        setIdToken("");
        setAuthReady(true);
        setAuthError("Please log in again to view this Contract Insight job.");
        return;
      }

      try {
        const token = await user.getIdToken();
        idTokenRef.current = token;
        setIdToken(token);
        setAuthReady(true);
        setAuthError("");
      } catch (err) {
        console.error("Failed to fetch Firebase ID token for Contract Insight status:", err);
        idTokenRef.current = "";
        setIdToken("");
        setAuthReady(true);
        setAuthError("Unable to verify your login session. Please log in again.");
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!jobId) return undefined;
    if (!authReady) return undefined;

    let stop = null;
    let cancelled = false;

    if (!idTokenRef.current) {
      setError(authError || "Please log in again to view this Contract Insight job.");
      setLoading(false);
      return undefined;
    }

    (async () => {
      try {
        const firstToken = idTokenRef.current || idToken;
        const first = await getContractInsightStatus(jobId, {
          idToken: firstToken,
        });
        if (!cancelled) {
          setPayload(first);
          setError("");
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(FRIENDLY_STATUS_ERROR);
          setLoading(false);
        }
      }

      stop = pollReport(jobId, {
        intervalMs: 1500,
        idToken: idTokenRef.current || idToken,
        getIdToken: () => getFreshAuthToken(),
        onTick: (p) => {
          if (cancelled) return;
          if (p?.error) {
            setError((prev) => prev || FRIENDLY_STATUS_ERROR);
            return;
          }
          setPayload(p);
          setError("");
        },
      });
    })();

    return () => {
      cancelled = true;
      if (stop) stop();
    };
  }, [jobId, authReady, idToken, authError]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return undefined;

    const detectDevice = () => {
      const width = window.innerWidth || 1200;
      setIsDesktopLike(width >= 1024);
    };

    detectDevice();
    window.addEventListener("resize", detectDevice);
    return () => window.removeEventListener("resize", detectDevice);
  }, []);

  const job = payload?.job || {};
  const progress = job?.progress || payload?.progress || {};
  const rawStatus = job?.status || payload?.status || progress?.stage || (loading ? "queued" : "queued");
  const status = normalizeStatus(rawStatus, progress);
  const stageMeta = getStageMeta(status, progress);
  const activeDisplayIndex = displayStageIndex(status, progress);
  const progressPercent = Math.max(
    0,
    Math.min(100, Number(progress?.percent || payload?.progress?.percent || stageMeta.percent || 0))
  );
  const isDone = status === "complete";
  const isError = status === "error" || status === "billing_error";

  const contractType = job?.schemaId || (job?.schemaVersion ? `v${job.schemaVersion}` : "Auto / generic");
  const displayFileName = cleanDisplayText(
    pickFirst(
      job?.originalName,
      job?.original_name,
      job?.filename,
      job?.fileName,
      job?.file_name,
      payload?.originalName,
      payload?.original_name,
      payload?.filename,
      payload?.fileName,
      payload?.file_name,
      getByPath(payload, "job.originalName"),
      getByPath(payload, "job.original_name"),
      getByPath(payload, "job.filename"),
      getByPath(payload, "job.fileName"),
      getByPath(payload, "job.file_name"),
      source?.originalName,
      source?.original_name,
      source?.fileName,
      source?.file_name,
      source?.filename,
      source?.name,
      getByPath(job, "source.originalName"),
      getByPath(job, "source.original_name"),
      getByPath(job, "source.fileName"),
      getByPath(job, "source.file_name"),
      getByPath(job, "contractSnapshot.contractName"),
      getByPath(job, "contract_snapshot.contract_name"),
      getByPath(reportRoutePayload, "job.originalName"),
      getByPath(reportRoutePayload, "job.original_name"),
      getByPath(reportRoutePayload, "job.fileName"),
      getByPath(reportRoutePayload, "job.file_name"),
      getByPath(reportJsonBody, "metadata.originalName"),
      getByPath(reportJsonBody, "metadata.original_name"),
      getByPath(reportJsonBody, "contractSnapshot.contractName"),
      getByPath(reportJsonBody, "contract_snapshot.contract_name"),
      "Contract file"
    ),
    "Contract file"
  );
  const errorMessage = FRIENDLY_STATUS_ERROR;

  const originalSourceUrl = pickFirst(
    source?.originalUrl,
    source?.original_url,
    source?.sourceFileUrl,
    source?.source_file_url,
    source?.sourceUrl,
    source?.source_url,
    source?.url,
    job?.sourceFileUrl,
    job?.source_file_url,
    job?.sourcePdfUrl,
    job?.source_pdf_url,
    job?.sourceUrl,
    job?.source_url,
    payload?.sourceFileUrl,
    payload?.source_file_url,
    payload?.sourcePdfUrl,
    payload?.source_pdf_url,
    payload?.sourceUrl,
    payload?.source_url
  );

  const analyzedPdfUrl = pickFirst(
    getAnalyzedPdfUrlFromPayload(source),
    getAnalyzedPdfUrlFromPayload(payload),
    getAnalyzedPdfUrlFromPayload(job),
    getAnalyzedPdfUrlFromPayload(liveViewPayload),
    getAnalyzedPdfUrlFromPayload(reportRoutePayload)
  );

  const activeDocumentUrl =
    documentView === "original" ? originalSourceUrl : analyzedPdfUrl;
  const activeDocumentLabel =
    documentView === "original" ? "Original upload" : "Analyzed document";

  const livePages = asPages(liveView);
  const liveViewCompleteness =
    liveViewPayload?.liveViewCompleteness ||
    liveViewPayload?.live_view_completeness ||
    liveViewPayload?.liveView?.liveViewCompleteness ||
    liveViewPayload?.liveView?.live_view_completeness ||
    {};

  const liveMeta =
    liveViewPayload?.meta ||
    liveViewPayload?.metadata ||
    liveViewPayload?.liveView?.meta ||
    liveViewPayload?.live_view?.meta ||
    {};

  const liveViewPageCount = pickFirstNumeric(
    liveViewCompleteness?.pageCount,
    liveViewCompleteness?.page_count,
    liveViewCompleteness?.liveViewPageCount,
    liveViewCompleteness?.live_view_page_count,
    getByPath(liveViewCompleteness, "upstream.liveViewPageCount"),
    getByPath(liveViewCompleteness, "upstream.live_view_page_count"),
    getByPath(job, "liveViewCompleteness.pageCount"),
    getByPath(job, "liveViewCompleteness.liveViewPageCount"),
    getByPath(reportJsonBody, "liveViewCompleteness.pageCount"),
    getByPath(reportJsonBody, "liveViewCompleteness.liveViewPageCount"),
    livePages.length
  );

  const liveViewHighlightCount = pickFirstNumeric(
    liveViewCompleteness?.paragraphCount,
    liveViewCompleteness?.paragraph_count,
    liveViewCompleteness?.liveViewParagraphCount,
    liveViewCompleteness?.live_view_paragraph_count,
    getByPath(liveViewCompleteness, "upstream.liveViewParagraphCount"),
    getByPath(liveViewCompleteness, "upstream.live_view_paragraph_count"),
    getByPath(job, "liveViewCompleteness.paragraphCount"),
    getByPath(job, "liveViewCompleteness.liveViewParagraphCount"),
    getByPath(reportJsonBody, "liveViewCompleteness.paragraphCount"),
    getByPath(reportJsonBody, "liveViewCompleteness.liveViewParagraphCount"),
    countParagraphsFromPages(livePages)
  );

  const hasLiveViewHighlights =
    liveViewHighlightCount !== "" &&
    liveViewHighlightCount !== undefined &&
    liveViewHighlightCount !== null;

  const liveDashboardSummary = pickFirst(
    extractSummaryTextFromObject(liveViewPayload),
    extractSummaryTextFromObject(liveMeta),
    extractSummaryTextFromObject(liveView),
    extractSummaryTextFromObject(liveViewPayload?.liveView),
    extractSummaryTextFromObject(liveViewPayload?.live_view)
  );

  const reportDashboardSummary = extractSummaryTextFromObject(
    reportRoutePayload
  );

  const dashboardSummaryText = pickFirst(
    liveDashboardSummary,
    reportDashboardSummary,
    job?.dashboardSummary,
    job?.dashboard_summary,
    job?.executiveSummary,
    job?.executive_summary,
    job?.overallFinding,
    job?.overall_finding,
    job?.summary,
    getByPath(job, "report.summary.overallFinding"),
    getByPath(job, "report.summary.overall_finding"),
    getByPath(job, "report.summary.executiveSummary"),
    getByPath(job, "report.summary.executive_summary"),
    getByPath(job, "summary.overallFinding"),
    getByPath(job, "summary.overall_finding"),
    getByPath(job, "summary.executiveSummary"),
    getByPath(job, "summary.executive_summary"),
    buildLiveViewSummary({
      pageCount: liveViewPageCount,
      paragraphCount: liveViewHighlightCount,
      ready: isDone,
    })
  );

  const coverageSnapshot = getCoverageSnapshotFromPayload(
    reportRoutePayload
  );

  const reportStats = [
    {
      title: "Found",
      value: coverageSnapshot.found,
      color: "#047857",
      background: "#ECFDF5",
      border: "#A7F3D0",
    },
    {
      title: "Unclear",
      value: coverageSnapshot.unclear,
      color: "#6D28D9",
      background: "#F5F3FF",
      border: "#DDD6FE",
    },
    {
      title: "Not Confirmed",
      value: coverageSnapshot.notConfirmed,
      color: "#B45309",
      background: "#FFFBEB",
      border: "#FDE68A",
    },
    {
      title: "Not Found",
      value: coverageSnapshot.notFound,
      color: "#B91C1C",
      background: "#FEF2F2",
      border: "#FECACA",
    },
    {
      title: "Live View Highlights",
      value: hasLiveViewHighlights ? liveViewHighlightCount : null,
      label:
        liveViewPageCount !== ""
          ? `Across ${liveViewPageCount} page${Number(liveViewPageCount) === 1 ? "" : "s"}`
          : "Clause review coverage",
      color: "#2563EB",
      background: "#EFF6FF",
      border: "#BFDBFE",
    },
  ];

  const previewPaneHeight = isDesktopLike ? 672 : 548;
  const previewViewerHeight = isDesktopLike ? 560 : 460;

  useEffect(() => {
    if (!jobId || !isDone || !authReady) return undefined;

    let cancelled = false;
    setLiveViewLoading(true);
    setLiveViewError("");

    (async () => {
      try {
        const token = await getFreshAuthToken();
        const res = await getLiveView(jobId, { idToken: token });
        const raw = res?.liveView || res?.thread || res || {};
        const lv = (raw && raw.liveView) || (raw && raw.live_view) || raw;

        if (!cancelled) {
          setLiveViewPayload(res || {});
          setLiveView(lv);
        }
      } catch (e) {
        if (!cancelled) {
          setLiveViewError(FRIENDLY_PREVIEW_ERROR);
        }
      } finally {
        if (!cancelled) {
          setLiveViewLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, isDone, authReady]);

  useEffect(() => {
    if (!jobId || !isDone || !authReady) return undefined;

    let cancelled = false;
    setReportPayloadLoading(true);
    setReportPayloadError("");

    (async () => {
      try {
        const token = await getFreshAuthToken();
        const data = await getReport(jobId, { idToken: token });
        if (cancelled) return;

        setReportRoutePayload(data || {});
        const embeddedBody = getEmbeddedReportBody(data || {});

        if (embeddedBody) {
          setReportJsonBody(embeddedBody);
          return;
        }

        const reportJsonUrl = getReportJsonUrlFromPayload(data || {});
        if (reportJsonUrl) {
          try {
            const body = await fetchReportJsonBodyFromSignedUrl(reportJsonUrl);
            if (!cancelled) setReportJsonBody(body || null);
          } catch (fetchErr) {
            if (!cancelled) {
              setReportPayloadError(FRIENDLY_SUMMARY_ERROR);
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          setReportRoutePayload(null);
          setReportJsonBody(null);
          setReportPayloadError(FRIENDLY_SUMMARY_ERROR);
        }
      } finally {
        if (!cancelled) setReportPayloadLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, isDone, authReady]);

  useEffect(() => {
    if (!jobId || !isDone || !authReady) return undefined;

    let cancelled = false;
    setSourceLoading(true);
    setSourceError("");

    (async () => {
      try {
        const token = await getFreshAuthToken();
        const res = await getSourceFile(jobId, { idToken: token });
        if (!cancelled) {
          const hasOriginalUrl = Boolean(
            pickFirst(
              res?.originalUrl,
              res?.original_url,
              res?.sourceFileUrl,
              res?.source_file_url,
              res?.sourceUrl,
              res?.source_url,
              res?.url
            )
          );
          const hasAnalyzedUrl = Boolean(getAnalyzedPdfUrlFromPayload(res));

          if ((res?.ok || hasOriginalUrl || hasAnalyzedUrl) && (hasOriginalUrl || hasAnalyzedUrl)) {
            setSource(res);
          } else {
            setSourceError("The original contract preview is not available right now.");
          }
        }
      } catch (e) {
        if (!cancelled) {
          setSourceError(FRIENDLY_PREVIEW_ERROR);
        }
      } finally {
        if (!cancelled) {
          setSourceLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, isDone, authReady]);

  const splitColumns =
    desktopViewMode === "both"
      ? "minmax(0, 1.08fr) minmax(0, 1fr)"
      : "minmax(0,1fr)";

  const statusSubtitle = isError
    ? errorMessage
    : isDone
      ? "Your report is ready. You can review the live highlights below or open the PDF report."
      : "This page updates automatically while your contract is reviewed, the report is prepared, and the Live View is saved.";

  const renderContractPanel = (withExpandControls = false) => (
    <div style={{ ...styles.splitPanel, height: previewPaneHeight, minHeight: previewPaneHeight }}>
      <div style={styles.panelHeaderRow}>
        <div>
          <h3 style={styles.splitTitle}>{activeDocumentLabel}</h3>
          <p style={styles.panelSubtext}>
            {documentView === "analyzed"
              ? "Review the PDF created for Contract Insight analysis."
              : "Review the file originally uploaded by the user."}
          </p>
        </div>
        {withExpandControls && (
          <button
            type="button"
            style={styles.panelToggleBtn}
            onClick={() =>
              setDesktopViewMode((mode) =>
                mode === "contract" ? "both" : "contract"
              )
            }
          >
            {desktopViewMode === "contract" ? "Restore split" : "Expand"}
          </button>
        )}
      </div>

      <div
        className="contract-status-document-toggle"
        style={styles.documentToggleWrap}
        role="group"
        aria-label="Choose contract document"
      >
        <button
          type="button"
          className="contract-status-document-toggle-button"
          aria-pressed={documentView === "analyzed"}
          data-selected={documentView === "analyzed" ? "true" : "false"}
          style={{
            ...styles.documentToggleButton,
            ...(documentView === "analyzed" ? styles.documentToggleButtonActive : {}),
          }}
          onClick={() => selectDocumentView("analyzed")}
        >
          Analyzed document
        </button>
        <button
          type="button"
          className="contract-status-document-toggle-button"
          aria-pressed={documentView === "original"}
          data-selected={documentView === "original" ? "true" : "false"}
          style={{
            ...styles.documentToggleButton,
            ...(documentView === "original" ? styles.documentToggleButtonActive : {}),
          }}
          onClick={() => selectDocumentView("original")}
        >
          Original upload
        </button>
      </div>

      {sourceLoading && <p style={styles.dim}>Loading document…</p>}
      {documentView === "original" && sourceError && (
        <p style={styles.err}>⚠️ {sourceError}</p>
      )}

      {activeDocumentUrl ? (
        <div style={styles.pdfFrameWrap}>
          <ContractFileViewer
            key={`${documentView}:${activeDocumentUrl}`}
            url={activeDocumentUrl}
            height={previewViewerHeight}
          />
        </div>
      ) : (
        !sourceLoading && (
          <div style={styles.pdfPlaceholder}>
            <p style={{ margin: 0 }}>
              {documentView === "analyzed"
                ? "Analyzed PDF preview is not available. The backend response must include a signed ingest PDF URL."
                : "Original upload preview is not available yet."}
            </p>
          </div>
        )
      )}
    </div>
  );

  const renderLivePanel = (withExpandControls = false) => (
    <div style={{ ...styles.splitPanelSoft, height: previewPaneHeight, minHeight: previewPaneHeight }}>
      <div style={styles.panelHeaderRow}>
        <div>
          <h3 style={styles.splitTitle}>Live highlights</h3>
          <p style={styles.panelSubtext}>Plain-English contract sections, risks, and watch-outs.</p>
        </div>
        {withExpandControls && (
          <button
            type="button"
            style={styles.panelToggleBtn}
            onClick={() =>
              setDesktopViewMode((mode) =>
                mode === "live" ? "both" : "live"
              )
            }
          >
            {desktopViewMode === "live" ? "Restore split" : "Expand"}
          </button>
        )}
      </div>

      {liveViewLoading && <p style={styles.dim}>Loading highlights…</p>}
      {liveViewError && <p style={styles.err}>⚠️ {liveViewError}</p>}

      {liveView && Array.isArray(liveView.pages) && liveView.pages.length > 0 && (
        <div style={styles.tilesWrap}>
          {liveView.pages.map((page, idx) => {
            const pageNum = page.pageNumber || page.page || idx + 1;
            const pageKey = `page-${pageNum}`;
            const paras = page.paragraphs || [];
            if (!paras.length) return null;

            const isPageExpanded = expandedPageKey === pageKey;
            let pageHasAnyContent = false;

            return (
              <div key={pageKey} style={styles.pageGroup}>
                <div style={styles.pageHeaderRow}>
                  <div style={styles.pageHeader}>Page {pageNum}</div>
                  <button
                    type="button"
                    style={{
                      ...styles.pageExplainBtn,
                      ...(isPageExpanded ? styles.pageExplainBtnActive : {}),
                    }}
                    onClick={() => {
                      setExpandedPageKey((cur) => (cur === pageKey ? null : pageKey));
                    }}
                  >
                    {isPageExpanded ? "Hide explanation" : "Explain this page"}
                  </button>
                </div>

                {isPageExpanded && (
                  <div style={styles.pageExplainBox}>
                    <div style={styles.pageExplainTitle}>Plain English explainer for this page</div>
                    {paras.map((para, pIdx) => {
                      const explanationText =
                        typeof para.explanation === "string" ? para.explanation.trim() : "";
                      const watchOut = Array.isArray(para.watchOutFor)
                        ? para.watchOutFor.filter((w) => typeof w === "string" && w.trim().length > 0)
                        : [];
                      const effectiveExplanation =
                        explanationText ||
                        (typeof para.summary === "string" ? para.summary : "").trim();

                      if (!effectiveExplanation && !watchOut.length) return null;
                      pageHasAnyContent = true;

                      return (
                        <div key={`${pageKey}-exp-${pIdx}`} style={styles.pageExplainItem}>
                          <div style={styles.pageExplainHeading}>{para.heading || `Section ${pIdx + 1}`}</div>
                          {effectiveExplanation && (
                            <p style={styles.pageExplainText}>{effectiveExplanation}</p>
                          )}
                          {watchOut.length > 0 && (
                            <>
                              <div style={styles.pageExplainSubLabel}>What to watch for</div>
                              <ul style={styles.pageExplainBullets}>
                                {watchOut.map((w, wi) => (
                                  <li key={wi} style={styles.pageExplainBullet}>
                                    {w}
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      );
                    })}
                    {!pageHasAnyContent && (
                      <p style={styles.pageExplainEmpty}>No additional explanation is available for this page yet.</p>
                    )}
                  </div>
                )}

                <div style={styles.tilesGrid}>
                  {paras.map((para, pIdx) => {
                    const level = (para.riskLevel || "").toLowerCase().trim();
                    const riskTileStyle = styles[`tileRisk_${level}`] || {};
                    const riskPillStyle = styles[`tileRiskPill_${level}`] || {};
                    const tileKey = (para.paraId && `${pageNum}-${para.paraId}`) || `${pageNum}-${pIdx}`;
                    const isExpanded = expandedTileKey === tileKey;
                    const explanationText = typeof para.explanation === "string" ? para.explanation.trim() : "";
                    const watchOut = Array.isArray(para.watchOutFor)
                      ? para.watchOutFor.filter((w) => typeof w === "string" && w.trim().length > 0)
                      : [];

                    return (
                      <div
                        key={para.paraId || `${pageNum}-${pIdx}`}
                        style={{
                          ...styles.tile,
                          ...riskTileStyle,
                          ...(isExpanded ? styles.tileExpanded : {}),
                        }}
                        onClick={() => setExpandedTileKey((cur) => (cur === tileKey ? null : tileKey))}
                      >
                        <div style={styles.tileHeading}>
                          {hideLiveViewParagraphReference(para.heading, para.paraId) || "Untitled section"}
                        </div>
                        <div style={styles.tileSummary}>{para.summary || "No summary available for this paragraph."}</div>
                        {isExpanded && (
                          <div style={styles.tileExpandedBody} onClick={(e) => e.stopPropagation()}>
                            <div style={styles.tileSectionLabel}>Plain English explainer</div>
                            <div style={styles.tileExplanationText}>
                              {explanationText || "No additional plain-English explanation was provided for this section."}
                            </div>

                            {watchOut.length > 0 && (
                              <>
                                <div style={styles.tileSectionLabel}>What to watch for</div>
                                <ul style={styles.tileBullets}>
                                  {watchOut.map((w, wi) => (
                                    <li key={wi} style={styles.tileBullet}>
                                      {w}
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </div>
                        )}

                        {para.riskLevel && (
                          <div style={styles.tileMetaRow}>
                            <span style={{ ...styles.tileRiskPill, ...riskPillStyle }}>
                              {para.riskLevel}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!liveViewLoading &&
        !liveViewError &&
        (!liveView || !Array.isArray(liveView.pages) || liveView.pages.length === 0) && (
          <p style={styles.dim}>Highlights will appear here once available for this contract.</p>
        )}
    </div>
  );

  if (!authReady) {
    return (
      <div className="contract-status-page" style={styles.outerWrap}>
        <style>{pageCss}</style>
        <div style={styles.loadingCard}>
          <Spinner />
          <div style={styles.loadingTitle}>Preparing Contract Insight status…</div>
        </div>
      </div>
    );
  }

  if (!idToken && !idTokenRef.current) {
    return (
      <div className="contract-status-page" style={styles.outerWrap}>
        <style>{pageCss}</style>
        <section style={styles.emptyState}>
          <div style={styles.emptyIcon}>◇</div>
          <h1 style={styles.emptyTitle}>Sign in to view this status</h1>
          <p style={styles.emptyText}>Contract Insight analyses are saved under your account.</p>
          <Link to="/login" style={styles.primaryButton}>Sign in</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="contract-status-page" style={styles.outerWrap}>
      <style>{pageCss}</style>

      <header className="contract-status-hero" style={styles.hero}>
        <div style={styles.heroTextWrap}>
          <div style={styles.kicker}>Contract Insight</div>
          <h1 className="contract-status-title" style={styles.title}>Contract Insight Status</h1>
          <p className="contract-status-subtitle" style={styles.subtitle}>{statusSubtitle}</p>
        </div>

        <div style={styles.heroStatusCard}>
          <div style={styles.orbitShell(stageMeta, progressPercent)}>
            <div style={styles.orbitInner}>
              <span style={styles.orbitValue}>{progressPercent}%</span>
            </div>
          </div>
          <div style={styles.heroStatusTextWrap}>
            <div style={styles.heroStatusLabel}>Current status</div>
            <div style={styles.heroStatusValue(stageMeta)}>
              <span style={styles.heroStatusDot(stageMeta)} />
              {friendly(status, progress)}
            </div>
          </div>
        </div>
      </header>

      <main style={styles.shell}>
        {authError && !idTokenRef.current && <p style={styles.err}>⚠️ {authError}</p>}
        {error && <p style={styles.err}>⚠️ {error}</p>}

        {jobId && (
          <>
            <section style={styles.progressPanel}>
              <div className="contract-status-progress-head" style={styles.progressHead}>
                <div style={styles.progressCopy}>
                  <h2 style={styles.sectionTitle}>Analysis progress</h2>
                  <p style={styles.sectionSubtitle}>
                    {isError ? "Open the message below and retry from the upload page if needed." : stageMeta.text}
                  </p>
                </div>
                <span style={styles.stagePill(stageMeta)}>
                  <span style={styles.stagePillDot(stageMeta)} />
                  {stageMeta.shortLabel}
                </span>
              </div>

              {!isDone && !isError ? (
                <div style={styles.processingNote}>
                  <div className="contract-status-processing-note-head" style={styles.processingNoteHead}>
                    <div style={styles.processingNoteLead}>
                      <span style={styles.processingNoteIcon}>✓</span>
                      <strong style={styles.processingNoteTitle}>Your analysis is continuing in the background.</strong>
                    </div>
                    <button
                      type="button"
                      className="contract-status-processing-note-toggle"
                      onClick={() => setShowProcessingDetails((value) => !value)}
                      style={styles.processingNoteToggle}
                      aria-expanded={showProcessingDetails}
                    >
                      {showProcessingDetails ? "Hide details" : "Show details"}
                    </button>
                  </div>

                  {showProcessingDetails ? (
                    <p style={styles.processingNoteText}>
                      You may safely leave this page or close your browser and return anytime through Contract Insight Analysis History. Larger contracts can take additional time while the file is reviewed, the report is prepared, the PDF is saved, and the Live View is created.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div style={styles.instrumentPanel}>
                <div style={styles.instrumentTop}>
                  <div>
                    <div style={styles.instrumentLabel}>Overall progress</div>
                    <div style={styles.instrumentSub}>
                      {loading ? "Loading analysis status…" : `${Math.round(progressPercent)}% complete`}
                    </div>
                  </div>
                  <div style={{ ...styles.instrumentPercent, color: stageMeta.color }}>
                    {Math.round(progressPercent)}%
                  </div>
                </div>
                <div style={styles.segmentTrack}>
                  <div
                    style={{
                      ...styles.segmentFill,
                      width: `${progressPercent}%`,
                      background: isError
                        ? "#DC2626"
                        : "linear-gradient(90deg, #2563EB 0%, #0EA5E9 24%, #8B5CF6 48%, #F59E0B 74%, #059669 100%)",
                    }}
                  />
                </div>
                <div className="contract-status-segment-labels" style={styles.segmentLabels}>
                  <span>Received</span>
                  <span>Reading</span>
                  <span>Analyzing</span>
                  <span>Report</span>
                  <span>Ready</span>
                </div>
              </div>

              <div className="contract-status-stage-grid" style={styles.stageGrid}>
                {STATUS_DISPLAY_STEPS.map((step, idx) => {
                  const done = isDone || activeDisplayIndex > idx;
                  const current = activeDisplayIndex === idx && !isDone && !isError;
                  const pending = !done && !current;

                  return (
                    <div
                      key={step.key}
                      style={{
                        ...styles.stageCard,
                        ...(done ? styles.stageDone : null),
                        ...(current ? styles.stageCurrent : null),
                        ...(pending ? styles.stagePending : null),
                        borderColor: done || current ? `${step.color}44` : "rgba(15,23,42,0.08)",
                        boxShadow: current ? `0 0 0 2px ${step.color}18, 0 14px 28px rgba(15, 23, 42, 0.08)` : "none",
                      }}
                    >
                      <div style={styles.stageTop}>
                        <span
                          style={{
                            ...styles.stageIcon,
                            background: done || current ? step.color : "#E2E8F0",
                            color: done || current ? "#FFFFFF" : "#64748B",
                          }}
                        >
                          {done ? "✓" : idx + 1}
                        </span>
                        {current ? <Spinner size={13} color={step.color} /> : null}
                      </div>
                      <div style={styles.stageLabel}>{step.label}</div>
                    </div>
                  );
                })}
              </div>

              <div className="contract-status-meta-grid" style={styles.metaGrid}>
                <MetaCard label="Analysis ID" value={formatJobShort(jobId)} mono />
                <MetaCard label="File name" value={displayFileName} />
                <MetaCard label="Contract type" value={contractType} />
                <MetaCard label="Status" value={friendly(status, progress)} accent={stageMeta.color} />
              </div>
            </section>



            {isError && (
              <section style={styles.errorBanner}>
                <div style={styles.errorIcon}>!</div>
                <div>
                  <h2 style={styles.errorTitle}>Something needs attention</h2>
                  <p style={styles.errorText}>{errorMessage}</p>
                  <Link to="/contract-insight" style={styles.retryButton}>Try again</Link>
                </div>
              </section>
            )}

            {isDone && (
              <>
                <section style={styles.resultBox}>
                  <div>
                    <h2 style={styles.resultTitle}>Report ready</h2>
                    <p style={styles.resultText}>
                      Your Contract Insight report has been generated and stored safely in your File Vault. The dashboard summary and Live View below are ready for review.
                    </p>
                  </div>
                  <div style={styles.links}>
                    <button
                      type="button"
                      onClick={openSummaryReport}
                      disabled={Boolean(artifactLoading)}
                      style={{
                        ...styles.linkBtnPrimary,
                        border: "none",
                        cursor: artifactLoading ? "not-allowed" : "pointer",
                        opacity: artifactLoading ? 0.7 : 1,
                      }}
                      title="Open the Summary Report through AskLawly File Access"
                    >
                      {artifactLoading === "summary-pdf" ? "Opening…" : "Open PDF Report"}
                    </button>
                    <Link
                      to={`/contract-insight/dashboard?jobId=${encodeURIComponent(jobId)}`}
                      style={{ ...styles.linkBtnGhost, textDecoration: "none" }}
                    >
                      Open Dashboard
                    </Link>
                    <Link to="/file-vault" style={{ ...styles.linkBtnGhost, textDecoration: "none" }}>
                      Go to File Vault
                    </Link>
                  </div>
                </section>

                {artifactError ? (
                  <div style={styles.pendingSummaryBox}>⚠️ {artifactError}</div>
                ) : null}

                <section className="contract-status-score-grid" style={styles.scoreGrid}>
                  {reportStats.map((item) => (
                    <ContractInsightScoreCard
                      key={item.title}
                      title={item.title}
                      value={item.value}
                      label={item.label}
                      color={item.color}
                      background={item.background}
                      border={item.border}
                    />
                  ))}
                </section>

                <section style={styles.dashboardOverviewPanel}>
                  <div style={styles.dashboardOverviewHead}>
                    <h2 style={styles.dashboardSummaryTitle}>Dashboard summary</h2>
                  </div>

                  <p style={styles.dashboardSummaryText}>
                    {cleanDisplayText(dashboardSummaryText, "Dashboard summary will appear when the completed report is available.")}
                  </p>

                  {reportPayloadLoading ? (
                    <div style={styles.pendingSummaryBox}>Loading completed dashboard summary…</div>
                  ) : null}

                  {reportPayloadError ? (
                    <div style={styles.pendingSummaryBox}>⚠️ {reportPayloadError}</div>
                  ) : null}
                </section>
              </>
            )}

            {isDone && isDesktopLike && (
              <section style={{ ...styles.splitViewSection, gridTemplateColumns: splitColumns }}>
                {desktopViewMode !== "live" && renderContractPanel(true)}
                {desktopViewMode !== "contract" && renderLivePanel(true)}
              </section>
            )}

            {isDone && !isDesktopLike && (
              <section style={styles.mobileTabsSection}>
                <div style={styles.tabHeaderRow}>
                  <button
                    type="button"
                    style={{ ...styles.tabButton, ...(activeMobileTab === "contract" ? styles.tabButtonActive : {}) }}
                    onClick={() => setActiveMobileTab("contract")}
                  >
                    Contract
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.tabButton, ...(activeMobileTab === "live" ? styles.tabButtonActive : {}) }}
                    onClick={() => setActiveMobileTab("live")}
                  >
                    Live highlights
                  </button>
                </div>
                <div style={styles.tabBody}>
                  {activeMobileTab === "contract" ? renderContractPanel(false) : renderLivePanel(false)}
                </div>
              </section>
            )}

            <div style={styles.footerLinks}>
              <Link to="/contract-insight" style={styles.backLink}>← Back to New Analysis</Link>
              <Link to="/contract-insight/history" style={styles.backLink}>Analysis History</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function ContractInsightScoreCard({
  title,
  value,
  label = "",
  color = "#2563EB",
  background = "#FFFFFF",
  border = "rgba(148,163,184,0.22)",
}) {
  const hasValue = typeof value === "number" && Number.isFinite(value);
  const displayValue = hasValue ? Math.max(0, Math.trunc(value)) : "—";

  return (
    <article
      style={{
        ...styles.scoreCard,
        background,
        borderColor: border,
      }}
    >
      <div style={{ ...styles.scoreTitle, color }}>{title}</div>
      <div style={{ ...styles.coverageValue, color }}>{displayValue}</div>
      {label ? <div style={styles.scoreSubLabel}>{label}</div> : null}
    </article>
  );
}

function MetaCard({ label, value, mono, accent }) {
  return (
    <div style={styles.metaCard}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={{ ...styles.metaValue, ...(mono ? styles.mono : {}), ...(accent ? { color: accent } : {}) }}>
        {value || "—"}
      </div>
    </div>
  );
}

const pageCss = `
  @keyframes contractStatusSpin { to { transform: rotate(360deg); } }

  html, body, #root {
    max-width: 100%;
    overflow-x: hidden;
  }

  .contract-status-page,
  .contract-status-page *,
  .contract-status-hero,
  .contract-status-progress-head,
  .contract-status-processing-note-head,
  .contract-status-stage-grid,
  .contract-status-score-grid,
  .contract-status-dashboard-head,
  .contract-status-meta-grid,
  .contract-status-segment-labels {
    box-sizing: border-box;
    min-width: 0;
    max-width: 100%;
  }

  .contract-status-page {
    width: 100%;
    overflow-x: hidden;
    contain: inline-size;
  }

  @media (max-width: 1023px) {
    .contract-status-page {
      padding: 22px 12px 34px !important;
    }

    .contract-status-hero {
      display: block !important;
      text-align: center !important;
    }

    .contract-status-hero > div {
      margin-left: auto !important;
      margin-right: auto !important;
    }

    .contract-status-title {
      font-size: 28px !important;
      line-height: 1.18 !important;
      text-align: center !important;
      overflow-wrap: anywhere !important;
    }

    .contract-status-subtitle {
      margin-left: auto !important;
      margin-right: auto !important;
      text-align: center !important;
      overflow-wrap: anywhere !important;
    }

    .contract-status-progress-head {
      align-items: flex-start !important;
    }

    .contract-status-processing-note-head {
      align-items: flex-start !important;
      flex-direction: column !important;
    }

    .contract-status-stage-grid {
      grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }

    .contract-status-score-grid {
      grid-template-columns: repeat(2, minmax(0, 360px)) !important;
      justify-content: center !important;
      gap: 12px !important;
    }

    .contract-status-score-grid > :last-child {
      grid-column: 1 / -1 !important;
      justify-self: center !important;
      width: min(100%, 360px) !important;
    }

    .contract-status-meta-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
  }

  @media (max-width: 760px) {
    .contract-status-stage-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }

    .contract-status-segment-labels {
      font-size: 10px !important;
      gap: 4px !important;
    }
  }

  @media (max-width: 620px) {
    .contract-status-score-grid {
      grid-template-columns: minmax(0, 1fr) !important;
      width: 100% !important;
    }

    .contract-status-score-grid > :last-child {
      grid-column: auto !important;
      justify-self: stretch !important;
      width: 100% !important;
    }
  }

  @media (max-width: 560px) {
    .contract-status-meta-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
`;

const styles = {
  outerWrap: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: "100vh",
    overflowX: "hidden",
    background: "linear-gradient(135deg, #F8FAFC 0%, #EEF4FF 44%, #FFFFFF 100%)",
    color: "#0F172A",
    padding: "30px",
    boxSizing: "border-box",
  },
  hero: {
    width: "100%",
    maxWidth: 1500,
    minWidth: 0,
    margin: "0 auto 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
  },
  heroTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  kicker: {
    color: "#2563EB",
    fontWeight: 900,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    fontSize: 12,
    marginBottom: 8,
  },
  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.12,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.035em",
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    maxWidth: 720,
  },
  heroStatusCard: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    gap: 13,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid #E2E8F0",
    borderRadius: 20,
    padding: "12px 14px",
    boxShadow: "0 16px 36px rgba(15, 23, 42, 0.08)",
    marginTop: 2,
  },
  orbitShell: (stageMeta, progressPercent) => ({
    width: 58,
    height: 58,
    borderRadius: "999px",
    padding: 5,
    background: `conic-gradient(${stageMeta.color} 0deg, ${stageMeta.color} ${progressPercent * 3.6}deg, #E2E8F0 ${progressPercent * 3.6}deg, #E2E8F0 360deg)`,
    boxShadow: `0 0 0 5px ${stageMeta.color}12`,
    flex: "0 0 auto",
  }),
  orbitInner: {
    width: "100%",
    height: "100%",
    borderRadius: "999px",
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #E2E8F0",
  },
  orbitValue: {
    fontSize: 13,
    fontWeight: 950,
    color: "#0F172A",
  },
  heroStatusTextWrap: {
    minWidth: 130,
  },
  heroStatusLabel: {
    fontSize: 11,
    fontWeight: 850,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: ".06em",
    marginBottom: 4,
  },
  heroStatusValue: (stageMeta) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: stageMeta.color,
    fontSize: 14,
    fontWeight: 900,
  }),
  heroStatusDot: (stageMeta) => ({
    width: 9,
    height: 9,
    borderRadius: "999px",
    background: stageMeta.color,
    boxShadow: `0 0 0 4px ${stageMeta.color}18`,
    flex: "0 0 auto",
  }),
  shell: {
    width: "100%",
    maxWidth: 1500,
    minWidth: 0,
    margin: "0 auto",
  },
  loadingCard: {
    width: "min(520px, 100%)",
    margin: "90px auto 0",
    borderRadius: 20,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    boxShadow: "0 20px 42px rgba(15, 23, 42, 0.1)",
    padding: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    color: "#0F172A",
    fontWeight: 850,
  },
  loadingTitle: {
    fontSize: 15,
    fontWeight: 850,
  },
  emptyState: {
    width: "min(580px, 100%)",
    margin: "80px auto 0",
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 24,
    padding: 34,
    textAlign: "center",
    boxShadow: "0 20px 42px rgba(15, 23, 42, 0.1)",
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#EEF2FF",
    color: "#1E2A78",
    fontSize: 26,
    fontWeight: 900,
    marginBottom: 14,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 950,
    color: "#0F172A",
  },
  emptyText: {
    margin: "8px auto 18px",
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 430,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    padding: "0 16px",
    borderRadius: 12,
    background: "#1E2A78",
    color: "#FFFFFF",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 900,
  },
  progressPanel: {
    border: "1px solid #E2E8F0",
    borderRadius: 24,
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.08)",
    padding: 18,
    overflow: "hidden",
  },
  progressHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 14,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.02em",
  },
  sectionSubtitle: {
    margin: "5px 0 0",
    color: "#64748B",
    fontSize: 13,
    lineHeight: 1.45,
  },
  stagePill: (stageMeta) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
    borderRadius: 999,
    padding: "7px 10px",
    background: stageMeta.bg,
    border: `1px solid ${stageMeta.border}`,
    color: stageMeta.color,
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: ".04em",
  }),
  stagePillDot: (stageMeta) => ({
    width: 8,
    height: 8,
    borderRadius: "999px",
    background: stageMeta.color,
  }),
  progressCopy: {
    minWidth: 0,
  },
  processingNote: {
    border: "1px solid #BFDBFE",
    borderRadius: 18,
    background: "linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 100%)",
    padding: 14,
    marginBottom: 14,
  },
  processingNoteHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  processingNoteLead: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 9,
  },
  processingNoteIcon: {
    width: 26,
    height: 26,
    borderRadius: 999,
    background: "#DBEAFE",
    color: "#1D4ED8",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 950,
    flex: "0 0 auto",
  },
  processingNoteTitle: {
    color: "#0F172A",
    fontSize: 13,
    lineHeight: 1.35,
    fontWeight: 950,
  },
  processingNoteToggle: {
    border: "1px solid #93C5FD",
    borderRadius: 999,
    background: "#FFFFFF",
    color: "#1D4ED8",
    minHeight: 30,
    padding: "0 10px",
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  processingNoteText: {
    margin: "10px 0 0 35px",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.55,
  },
  instrumentPanel: {
    border: "1px solid #DBEAFE",
    borderRadius: 18,
    padding: 14,
    marginBottom: 14,
    background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
  },
  instrumentTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 10,
  },
  instrumentLabel: {
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
  },
  instrumentSub: {
    marginTop: 3,
    color: "#64748B",
    fontSize: 12,
    fontWeight: 750,
  },
  instrumentPercent: {
    fontSize: 24,
    fontWeight: 950,
    letterSpacing: "-0.04em",
    whiteSpace: "nowrap",
  },
  segmentTrack: {
    height: 12,
    borderRadius: 999,
    background: "#E2E8F0",
    overflow: "hidden",
  },
  segmentFill: {
    height: "100%",
    borderRadius: 999,
    transition: "width 0.25s ease",
  },
  segmentLabels: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 8,
    marginTop: 8,
    color: "#64748B",
    fontSize: 11,
    fontWeight: 850,
    textAlign: "center",
  },
  stageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
    marginBottom: 12,
  },
  stageCard: {
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 16,
    background: "#FFFFFF",
    padding: "11px 12px",
    minHeight: 88,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "border-color .2s ease, background .2s ease, box-shadow .2s ease",
  },
  stageDone: {
    background: "#F8FAFC",
  },
  stageCurrent: {
    background: "#FFFFFF",
  },
  stagePending: {
    background: "#FFFFFF",
    opacity: 0.78,
  },
  stageTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 8,
  },
  stageIcon: {
    width: 28,
    height: 28,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 950,
    flex: "0 0 auto",
  },
  stageLabel: {
    color: "#0F172A",
    fontSize: 12,
    fontWeight: 950,
    lineHeight: 1.25,
    overflowWrap: "anywhere",
  },
  progressSummary: {
    border: "1px solid #DBEAFE",
    borderRadius: 18,
    padding: 14,
    marginBottom: 14,
    background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
  },
  progressTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 10,
  },
  progressLabel: {
    fontSize: 16,
    fontWeight: 950,
    color: "#0F172A",
  },
  progressText: {
    fontSize: 13,
    color: "#64748B",
    marginTop: 3,
    lineHeight: 1.4,
  },
  progressPercent: {
    fontSize: 22,
    fontWeight: 950,
    color: "#1E2A78",
    whiteSpace: "nowrap",
  },
  progressTrack: {
    height: 10,
    borderRadius: 999,
    background: "#E2E8F0",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
    transition: "width 0.25s ease",
  },
  stepperScroll: {
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    padding: "2px 2px 10px",
    scrollbarWidth: "thin",
  },
  stepper: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    alignItems: "stretch",
    gap: 10,
    minWidth: 1040,
  },
  step: {
    position: "relative",
    flex: "1 1 0",
    minWidth: 108,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 10px",
    border: "1px solid #E2E8F0",
    borderRadius: 14,
    color: "#475569",
    minHeight: 62,
    transition: "border-color .2s ease, background .2s ease, box-shadow .2s ease",
  },
  stepDot: {
    width: 27,
    height: 27,
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 950,
    lineHeight: "20px",
    flex: "0 0 auto",
  },
  stepTextWrap: {
    minWidth: 0,
  },
  stepLabel: {
    fontSize: 12,
    fontWeight: 900,
    color: "#0F172A",
    lineHeight: 1.2,
  },
  stepSub: {
    fontSize: 10.5,
    color: "#64748B",
    marginTop: 2,
    fontWeight: 750,
    textTransform: "uppercase",
    letterSpacing: ".03em",
  },
  metaGrid: {
    marginTop: 6,
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },
  metaCard: {
    border: "1px solid #E2E8F0",
    borderRadius: 16,
    background: "#FFFFFF",
    padding: "12px 13px",
    minHeight: 76,
  },
  metaLabel: {
    color: "#64748B",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: ".06em",
    marginBottom: 6,
  },
  metaValue: {
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: 12,
  },
  infoBanner: {
    marginTop: 16,
    border: "1px solid #BFDBFE",
    borderRadius: 20,
    background: "#EFF6FF",
    padding: 16,
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  infoIcon: {
    width: 38,
    height: 38,
    borderRadius: 14,
    background: "#DBEAFE",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
  },
  infoTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 950,
    color: "#0F172A",
  },
  infoText: {
    margin: "5px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.55,
  },
  errorBanner: {
    marginTop: 16,
    border: "1px solid #FECACA",
    borderRadius: 20,
    background: "#FEF2F2",
    padding: 16,
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  errorIcon: {
    width: 38,
    height: 38,
    borderRadius: 14,
    background: "#DC2626",
    color: "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    fontWeight: 950,
  },
  errorTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 950,
    color: "#7F1D1D",
  },
  errorText: {
    margin: "5px 0 12px",
    color: "#991B1B",
    fontSize: 13,
    lineHeight: 1.55,
  },
  retryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 36,
    padding: "0 12px",
    borderRadius: 10,
    background: "#DC2626",
    color: "#FFFFFF",
    textDecoration: "none",
    fontSize: 12,
    fontWeight: 900,
  },
  resultBox: {
    marginTop: 16,
    border: "1px solid #BBF7D0",
    background: "linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%)",
    color: "#065F46",
    borderRadius: 20,
    padding: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  resultTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 950,
    color: "#064E3B",
  },
  resultText: {
    margin: "5px 0 0",
    color: "#047857",
    fontSize: 13,
    lineHeight: 1.5,
    maxWidth: 760,
  },
  links: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-end",
  },
  linkBtnPrimary: {
    minHeight: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 13px",
    borderRadius: 12,
    background: "#1E2A78",
    color: "#FFFFFF",
    fontWeight: 900,
    fontSize: 13,
    textDecoration: "none",
  },
  linkBtnGhost: {
    minHeight: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 13px",
    borderRadius: 12,
    background: "#FFFFFF",
    border: "1px solid #CBD5E1",
    color: "#1E2A78",
    fontWeight: 900,
    fontSize: 13,
  },
  dashboardOverviewPanel: {
    marginTop: 16,
    border: "1px solid #E2E8F0",
    borderRadius: 24,
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.08)",
    padding: 18,
    overflow: "hidden",
  },
  dashboardOverviewHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  },
  dashboardSummaryTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.02em",
  },
  scoreGrid: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "16px auto 18px",
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    justifyContent: "center",
    gap: 12,
  },
  scoreCard: {
    minWidth: 0,
    minHeight: 92,
    overflow: "hidden",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 16,
    padding: "13px 10px",
    background: "#FFFFFF",
    textAlign: "center",
    display: "grid",
    alignContent: "center",
    gap: 8,
    boxShadow: "0 10px 24px rgba(15,23,42,0.045)",
  },
  scoreTitle: {
    minHeight: 28,
    color: "#0F172A",
    fontSize: 11.5,
    fontWeight: 950,
    lineHeight: 1.25,
    display: "grid",
    placeItems: "center",
    overflowWrap: "anywhere",
  },
  coverageValue: {
    fontSize: 28,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "-0.035em",
  },
  scoreSubLabel: {
    color: "#64748B",
    fontWeight: 750,
    fontSize: 11,
    lineHeight: 1.3,
    overflowWrap: "anywhere",
  },

  dashboardSummaryText: {
    margin: 0,
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.7,
  },
  pendingSummaryBox: {
    marginTop: 12,
    border: "1px solid #BFDBFE",
    background: "#EFF6FF",
    color: "#1D4ED8",
    borderRadius: 14,
    padding: 12,
    fontSize: 12,
    fontWeight: 800,
  },
  err: {
    color: "#B00020",
    background: "#FFF1F2",
    border: "1px solid #FECDD3",
    padding: 12,
    borderRadius: 14,
    margin: "0 0 14px",
    fontSize: 13,
    fontWeight: 700,
  },
  dim: {
    color: "#64748B",
    fontSize: 12,
    marginTop: 10,
  },
  splitViewSection: {
    marginTop: 16,
    display: "grid",
    gap: 16,
    alignItems: "stretch",
  },
  splitPanel: {
    border: "1px solid #E2E8F0",
    borderRadius: 20,
    padding: 12,
    background: "#FFFFFF",
    minHeight: 360,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 14px 34px rgba(15,23,42,0.07)",
  },
  splitPanelSoft: {
    border: "1px solid #E2E8F0",
    borderRadius: 20,
    padding: 12,
    background: "#F8FAFC",
    minHeight: 360,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
  },
  panelHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 9,
  },
  documentToggleWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    alignItems: "stretch",
    alignSelf: "center",
    width: "100%",
    maxWidth: 430,
    gap: 4,
    padding: 4,
    marginBottom: 10,
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    background: "#F1F5F9",
  },
  documentToggleButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
    minHeight: 42,
    padding: "8px 12px",
    borderRadius: 9,
    border: "1px solid transparent",
    background: "transparent",
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "normal",
    textAlign: "center",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
    transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 80ms ease",
  },
  documentToggleButtonActive: {
    borderColor: "#1E2A78",
    background: "#1E2A78",
    color: "#FFFFFF",
    boxShadow: "0 3px 9px rgba(30,42,120,0.22)",
  },
  panelToggleBtn: {
    fontSize: 11,
    padding: "5px 9px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    cursor: "pointer",
    color: "#334155",
    fontWeight: 850,
    whiteSpace: "nowrap",
  },
  splitTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 950,
    color: "#0F172A",
  },
  panelSubtext: {
    margin: "3px 0 0",
    color: "#64748B",
    fontSize: 12,
    lineHeight: 1.4,
  },
  pdfFrameWrap: {
    flex: 1,
    minHeight: 0,
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
  },
  pdfPlaceholder: {
    flex: 1,
    borderRadius: 14,
    border: "1px dashed #CBD5F5",
    background: "#EEF2FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    fontSize: 13,
    color: "#4B5563",
    textAlign: "center",
  },
  mobileTabsSection: {
    marginTop: 16,
  },
  tabHeaderRow: {
    display: "flex",
    borderRadius: 999,
    background: "#E2E8F0",
    padding: 4,
    gap: 4,
  },
  tabButton: {
    flex: 1,
    borderRadius: 999,
    border: "none",
    background: "transparent",
    fontSize: 13,
    padding: "8px 10px",
    cursor: "pointer",
    color: "#334155",
    fontWeight: 800,
  },
  tabButtonActive: {
    background: "#FFFFFF",
    boxShadow: "0 1px 3px rgba(15,23,42,0.15)",
    fontWeight: 950,
    color: "#1E2A78",
  },
  tabBody: {
    marginTop: 10,
  },
  tilesWrap: {
    marginTop: 4,
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: 4,
  },
  pageGroup: {
    marginBottom: 12,
  },
  pageHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    marginBottom: 6,
  },
  pageHeader: {
    fontSize: 12,
    fontWeight: 950,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: ".05em",
  },
  pageExplainBtn: {
    fontSize: 11,
    padding: "5px 9px",
    borderRadius: 999,
    border: "1px solid #C7D2FE",
    background: "#EEF2FF",
    color: "#3730A3",
    cursor: "pointer",
    fontWeight: 850,
    whiteSpace: "nowrap",
  },
  pageExplainBtnActive: {
    background: "#3730A3",
    color: "#EEF2FF",
    borderColor: "#3730A3",
  },
  pageExplainBox: {
    borderRadius: 14,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    padding: 10,
    marginBottom: 8,
  },
  pageExplainTitle: {
    fontSize: 12,
    fontWeight: 950,
    color: "#0F172A",
    marginBottom: 4,
  },
  pageExplainItem: {
    paddingTop: 6,
    paddingBottom: 6,
    borderTop: "1px dashed #E2E8F0",
  },
  pageExplainHeading: {
    fontSize: 12,
    fontWeight: 850,
    color: "#111827",
    marginBottom: 3,
  },
  pageExplainText: {
    fontSize: 12,
    color: "#475569",
    margin: 0,
    lineHeight: 1.45,
  },
  pageExplainSubLabel: {
    marginTop: 6,
    fontSize: 11,
    fontWeight: 850,
    color: "#334155",
  },
  pageExplainBullets: {
    margin: "3px 0 0",
    paddingLeft: 18,
  },
  pageExplainBullet: {
    fontSize: 12,
    color: "#475569",
    marginBottom: 3,
  },
  pageExplainEmpty: {
    fontSize: 12,
    color: "#64748B",
    marginTop: 4,
  },
  tilesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
    gap: 9,
    alignItems: "stretch",
  },
  tile: {
    borderRadius: 14,
    padding: 10,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    fontSize: 12,
    cursor: "pointer",
    transition: "background 0.12s ease, box-shadow 0.12s ease",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  tileExpanded: {
    boxShadow: "0 0 0 2px rgba(30,64,175,0.22)",
    background: "#EFF6FF",
  },
  tileHeading: {
    fontSize: 13,
    fontWeight: 900,
    color: "#0F172A",
    marginBottom: 5,
  },
  tileSummary: {
    fontSize: 12,
    color: "#475569",
    marginBottom: 8,
    lineHeight: 1.45,
  },
  tileMetaRow: {
    marginTop: "auto",
    paddingTop: 8,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 7,
  },
  tileMetaTag: {
    display: "none",
    fontSize: 11,
    color: "#64748B",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  tileRiskPill: {
    fontSize: 10,
    fontWeight: 950,
    borderRadius: 999,
    padding: "3px 7px",
    border: "1px solid transparent",
    textTransform: "uppercase",
    letterSpacing: ".04em",
  },
  tileExpandedBody: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: "1px dashed #CBD5E1",
  },
  tileSectionLabel: {
    fontSize: 11,
    fontWeight: 900,
    color: "#334155",
    marginTop: 5,
    marginBottom: 3,
  },
  tileExplanationText: {
    fontSize: 12,
    color: "#475569",
    lineHeight: 1.45,
  },
  tileBullets: {
    margin: "3px 0 0",
    paddingLeft: 18,
  },
  tileBullet: {
    fontSize: 12,
    color: "#475569",
    marginBottom: 3,
  },
  tileRisk_high: {
    borderColor: "#FECACA",
    background: "#FEF2F2",
  },
  tileRisk_medium: {
    borderColor: "#FED7AA",
    background: "#FFFBEB",
  },
  tileRisk_low: {
    borderColor: "#BBF7D0",
    background: "#ECFDF5",
  },
  tileRiskPill_high: {
    background: "#B91C1C",
    color: "#FEF2F2",
    borderColor: "#B91C1C",
  },
  tileRiskPill_medium: {
    background: "#C2410C",
    color: "#FFFBEB",
    borderColor: "#C2410C",
  },
  tileRiskPill_low: {
    background: "#15803D",
    color: "#ECFDF5",
    borderColor: "#15803D",
  },
  footerLinks: {
    marginTop: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  backLink: {
    color: "#1E2A78",
    textDecoration: "none",
    fontWeight: 900,
    fontSize: 13,
  },
};
